import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import InputNumber from "../../../../components/InputNumber/InputNumber";
import InputShortText from "../../../../components/InputShortText/InputShortText";
import Notice from "../../../../components/Notice/Notice";
import SecondaryButton from "../../../../components/SecondaryButton/SecondaryButton";
import SmallCheckBox from "../../../../components/SmallCheckBox/SmallCheckBox";
import Subtitle from "../../../../components/Subtitle/Subtitle";
import TextArea from "../../../../components/TextArea/TextArea";
import TinyText from "../../../../components/TinyText/TinyText";
import { EmailHtmlDualEditor } from "../../../../components/EmailHtmlDualEditor/EmailHtmlDualEditor";
import {
  NotificationDeliveryToggle,
  visitDeliveryModeFromState,
  type VisitDeliveryMode,
} from "../../../../components/NotificationDeliveryToggle/NotificationDeliveryToggle";
import type { NotificationChannel } from "../../../../components/NotificationChannelToggle/NotificationChannelToggle";
import RemindersAPI from "../../../../api/RemindersAPI";
import { toastService } from "../../../../services/ToastService";
import { normalizeEmailBodyFormat, type EmailBodyFormat } from "../../../../types/emailBodyFormat";
import { isValidText } from "../../../../shared/validation";
import { UpdateCallback } from "../../sections/NotificationsSegment";
import { stableSignatureVisitReminder } from "../reminderConfigStableSignature";
import { useHtmlEmailLocked } from "../../../../hooks/useHtmlEmailLocked";

interface VisitReminderProps {
  update: UpdateCallback;
  defaultConfig?: VisitReminderConfig;
  /** When true, omit the section heading (shown by parent collapsible trigger). */
  hideSectionTitle?: boolean;
}

interface ReminderDetails {
  emailTitle?: string;
  text?: string;
  daysInAdvance?: number;
  emailBodyFormat?: EmailBodyFormat;
}

export interface VisitReminderConfig {
  enabled: boolean;
  type: "sms" | "email";
  details?: ReminderDetails;
}

export function VisitReminder({ update, defaultConfig, hideSectionTitle }: VisitReminderProps) {
  const [enabled, setEnabled] = useState(defaultConfig?.enabled ?? false);
  const [type, setType] = useState<NotificationChannel>(defaultConfig?.type ?? "sms");
  const [details, setDetails] = useState<ReminderDetails>(defaultConfig?.details || {});
  const [testLoading, setTestLoading] = useState(false);
  const { t } = useTranslation();
  /** Skip re-applying parent props when they are still an echo of our last update() (avoids Plain/HTML flip). */
  const lastEmittedSignatureRef = useRef<string | null>(null);

  const htmlLocked = useHtmlEmailLocked();
  const storedEmailFmt = normalizeEmailBodyFormat(details.emailBodyFormat);
  const emailFmt =
    htmlLocked && type === "email" && storedEmailFmt === "html" ? "plain" : storedEmailFmt;
  const deliveryMode = visitDeliveryModeFromState(type, emailFmt);

  const handleDeliveryMode = (mode: VisitDeliveryMode) => {
    if (mode === "sms") {
      setType("sms");
      setDetails((d) => ({ ...d, emailBodyFormat: undefined }));
    } else if (mode === "plain_email") {
      setType("email");
      setDetails((d) => ({ ...d, emailBodyFormat: "plain" }));
    } else {
      setType("email");
      setDetails((d) => ({ ...d, emailBodyFormat: "html" }));
    }
  };

  useEffect(() => {
    if (!defaultConfig) return;
    const incoming = stableSignatureVisitReminder(defaultConfig);
    if (lastEmittedSignatureRef.current !== null && incoming === lastEmittedSignatureRef.current) {
      return;
    }
    setEnabled(defaultConfig.enabled || false);
    setType(defaultConfig.type || "sms");
    setDetails(defaultConfig.details || {});
  }, [defaultConfig]);

  useEffect(() => {
    const config: VisitReminderConfig = {
      enabled,
      type,
      details:
        type === "sms"
          ? { ...details, emailBodyFormat: undefined }
          : { ...details, emailBodyFormat: emailFmt },
    };
    lastEmittedSignatureRef.current = stableSignatureVisitReminder(config);
    update("smsReminder", config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, type, details]);

  const injectDefaultTemplate = () => {
    if (type === "sms") {
      setDetails({
        ...details,
        text: String(t("notifications.visit_reminder.default_sms_message")),
      });
      return;
    }
    const defaultBody =
      emailFmt === "html"
        ? String(t("notifications.visit_reminder.default_html"))
        : String(t("notifications.visit_reminder.default_message"));
    const baseDetails: ReminderDetails = {
      ...details,
      text: defaultBody,
      emailBodyFormat: emailFmt,
    };
    if (!details.emailTitle) {
      setDetails({
        ...baseDetails,
        emailTitle: String(t("notifications.visit_reminder.default_email_title")),
      });
    } else {
      setDetails(baseDetails);
    }
  };

  const sendTestEmail = async () => {
    if (type !== "email") return;
    const subject = (details.emailTitle ?? "").trim();
    const body = (details.text ?? "").trim();
    if (!subject || !body) {
      toastService.showToast(t("notifications.test_email_validation"), "error");
      return;
    }
    setTestLoading(true);
    try {
      await RemindersAPI.sendTestEmail({
        subject,
        htmlBody: body,
        emailBodyFormat: emailFmt,
      });
      toastService.showToast(t("notifications.test_email_ok"));
    } catch {
      toastService.showToast(t("notifications.test_email_fail"), "error");
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <>
      {!hideSectionTitle && <Subtitle text={t("notifications.visit_reminder")} />}
      <TinyText text={t("notifications.visit_reminder.description")} />
      <SmallCheckBox
        label={t("global.enabled")!}
        onChange={() => setEnabled((prev) => !prev)}
        defaultValue={enabled}
      />
      <br />
      <InputNumber
        label={t("notifications.visit_reminder.days_in_advance")!}
        validationFn={(value) => !!value && value >= 1}
        onChange={(e) => setDetails({ ...details, daysInAdvance: Number(e.currentTarget.value) })}
        defaultValue={details.daysInAdvance || 2}
        errorMessage={"errors.minimum_zero"}
      />
      <div className="mt-3">
        <p className="text-sm font-extrabold">{t("notifications.delivery.label")}:</p>
        <NotificationDeliveryToggle
          variant="three"
          className="mt-2"
          value={deliveryMode}
          onChange={handleDeliveryMode}
          htmlLocked={htmlLocked}
        />
      </div>
      <Notice pillText={t("global.notice")} tinyText={t("notifications.visit_reminder.message.keywords")} />
      {type === "sms" ? (
        <TextArea
          label={t("notifications.visit_reminder.sms_message")!}
          onChange={(e) => setDetails({ ...details, text: e.currentTarget.value })}
          defaultValue={details.text}
          errorMessage={"errors.too_long"}
          maxLength={100}
        />
      ) : (
        <div className="mt-3 space-y-3">
          <InputShortText
            label={t("notifications.visit_reminder.email_title")!}
            validationFn={(value) => value === "" || isValidText(value, 1, 50)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDetails({ ...details, emailTitle: e.target.value })
            }
            errorMessage={"errors.too_long"}
            defaultValue={details.emailTitle}
          />
          {emailFmt === "plain" ? (
            <TextArea
              label={t("notifications.visit_reminder.email_plain_label")!}
              onChange={(e) => setDetails({ ...details, text: e.currentTarget.value })}
              defaultValue={details.text}
              errorMessage={"errors.too_long"}
              maxLength={10000}
            />
          ) : (
            <div className="mt-1 space-y-1">
              <TinyText text={t("notifications.visit_reminder.email_html_help")} />
              <EmailHtmlDualEditor
                label={t("notifications.visit_reminder.email_html")!}
                value={details.text ?? ""}
                onChange={(html) => setDetails({ ...details, text: html })}
                maxLength={100000}
              />
            </div>
          )}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <SecondaryButton
          text={String(t("notifications.visit_reminder.use_default_template"))}
          fn={injectDefaultTemplate}
        />
        {type === "email" && (
          <SecondaryButton
            text={String(t("notifications.test_email"))}
            fn={sendTestEmail}
            isDisabled={testLoading}
          />
        )}
      </div>
    </>
  );
}
